import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Star, RotateCcw, ArrowRight, Loader2, Truck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { makeMixboxItem, tierUnitPrice, gbp } from "@/lib/pricing";
import { withLabels } from "@/pages/OrderBuilder";
import { useCategories } from "@/context/SiteContentContext";
import { AffiliatePanel } from "@/components/AffiliatePanel";
import { CombineUnpaid } from "@/components/CombineUnpaid";

const MODE_ROUTE = { build: "/build-your-box", deals: "/multipack-deals", guest: "/guest-collection", economy: "/economy-flavours" };

const mlLabel = (ml) => (ml >= 1000 ? `${(ml / 1000).toString().replace(/\.0$/, "")}L` : `${ml}ml`);

const STATUS_LABEL = {
  awaiting_payment: "Awaiting payment",
  paid: "Paid",
  failed: "Payment failed",
  cancelled: "Cancelled",
};
const FULFILMENT_LABEL = {
  pending: "Being prepared",
  processing: "Being prepared",
  shipped: "Shipped",
  delivered: "Delivered",
};

export default function TradeDashboard() {
  const cats = useCategories();
  const { user } = useAuth();
  const { addItem } = useCart();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get("/me/trade-dashboard"),
      api.get("/sizes"),
      api.get("/flavors"),
    ])
      .then(([d, s, f]) => {
        if (!live) return;
        setData(d.data);
        setSizes(s.data || []);
        setFlavors(f.data || []);
      })
      .catch(() => { if (live) toast.error("Could not load your dashboard. Please try again."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const sizeById = useMemo(() => Object.fromEntries(sizes.map((s) => [s.id, s])), [sizes]);
  const flavorById = useMemo(() => Object.fromEntries(flavors.map((f) => [f.id, f])), [flavors]);

  const reorder = (bundle, thenCheckout) => {
    const groups = {};
    (bundle.selections || []).forEach((s) => {
      const size = sizeById[s.size_id];
      if (!size) return;
      (groups[s.size_id] ||= { size, sel: {} });
      const fl = flavorById[s.flavor_id];
      const k = `${s.flavor_id}-${s.base}-${s.boost}`;
      groups[s.size_id].sel[k] ||= { flavor_id: s.flavor_id, flavor_name: fl?.name || "", base: s.base, boost: s.boost, quantity: 0 };
      groups[s.size_id].sel[k].quantity += s.quantity;
    });
    const entries = Object.values(groups);
    if (!entries.length) { toast.error("This saved order is no longer available"); return; }
    entries.forEach(({ size, sel }) => addItem(makeMixboxItem(size, Object.values(sel), { tiers: size.tiers })));
    toast.success(`Added “${bundle.name}” to your basket`);
    nav(thenCheckout ? "/checkout" : "/cart");
  };

  if (loading) {
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-[1100px] items-center justify-center px-6" data-testid="trade-dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const acct = data?.account || {};
  const saved = data?.saved_bundles || [];
  const orders = data?.orders || [];
  const restock = data?.restock;
  const restockBundle = restock?.bundle;
  const restockDays = restock?.days_since_last_order;
  const restockCopy = restock
    ? (restock.times_ordered > 1
        ? `You've ordered this ${restock.times_ordered} times — running low?`
        : restockDays != null
          ? `It's been ${restockDays} day${restockDays === 1 ? "" : "s"} since your last order — running low?`
          : "Your go-to mix, ready in one tap.")
    : "";

  return (
    <div className="mx-auto max-w-[1100px] px-6 pb-24 pt-10" data-testid="trade-dashboard">
      <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">Trade Account</div>
      <h1 className="mt-2 font-heading text-3xl font-black tracking-tighter sm:text-4xl" data-testid="trade-welcome">
        Welcome back, {acct.name || user?.name || "trade partner"}
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        {acct.discount_pct > 0
          ? `Your ${acct.discount_pct % 1 === 0 ? acct.discount_pct : acct.discount_pct.toFixed(1)}% trade pricing is applied automatically everywhere you shop.`
          : "Your trade pricing is applied automatically everywhere you shop."}
      </p>

      {restockBundle && (
        <div data-testid="trade-restock" className="mt-6 flex flex-col gap-3 border border-primary/40 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <RotateCcw className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-primary">Time to restock?</div>
              <div className="mt-1 font-medium" data-testid="trade-restock-name">{restockBundle.name} · {mlLabel(restockBundle.target_ml)}</div>
              <div className="text-xs text-muted-foreground">{restockCopy}</div>
            </div>
          </div>
          <button data-testid="trade-restock-reorder" onClick={() => reorder(restockBundle, true)}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 bg-primary px-5 py-2.5 text-xs font-medium text-primary-foreground transition-transform hover:-translate-y-0.5">
            Re-order now <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="mt-8">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">Start an order</div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="trade-order-options">
          {withLabels(cats).filter((o) => o.mode !== "deals").map((o) => {
            const Icon = o.icon;
            return (
              <Link
                key={o.mode}
                to={MODE_ROUTE[o.mode]}
                data-testid={`trade-order-${o.mode}`}
                className="group relative flex flex-col overflow-hidden border border-border bg-card p-6 text-left transition-colors hover:border-primary"
              >
                {o.mode === "economy" && (
                  <span className="mono absolute right-0 top-0 bg-primary px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.15em] text-primary-foreground">New</span>
                )}
                <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-primary transition-transform duration-300 group-hover:scale-x-100" />
                <span className="flex h-11 w-11 items-center justify-center border border-primary/40 bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="mono mt-4 text-[10px] uppercase tracking-[0.2em] text-primary">{o.tagline}</div>
                <div className="mt-1 font-heading text-xl font-black tracking-tight">{o.title}</div>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{o.desc}</p>
                <span className="mono mt-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
                  Start <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Usual orders */}
      <AffiliatePanel />
      <section className="mt-10" data-testid="trade-usual-orders">
        <h2 className="font-heading text-lg font-black tracking-tight">Your usual orders</h2>
        {saved.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="trade-usual-empty">
            You haven't saved any usual orders yet. Build a multipack and tap “Save as usual” to re-order it here in one tap.
          </p>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {saved.map((b) => (
              <div key={b.id} data-testid={`trade-usual-${b.id}`} className="flex flex-col border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      {b.pinned && <Star className="h-3.5 w-3.5 shrink-0 text-primary" fill="currentColor" />}
                      <span className="truncate font-medium">{b.name}</span>
                    </div>
                    <div className="mono mt-0.5 text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                      {mlLabel(b.target_ml)} · {(b.selections || []).reduce((a, s) => a + (s.quantity || 0), 0)} bottles
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button data-testid={`trade-reorder-cart-${b.id}`} onClick={() => reorder(b, false)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 border border-border py-2 text-xs font-medium transition-colors hover:border-primary">
                    <RotateCcw className="h-3.5 w-3.5" /> Add to basket
                  </button>
                  <button data-testid={`trade-reorder-checkout-${b.id}`} onClick={() => reorder(b, true)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 bg-primary py-2 text-xs font-medium text-primary-foreground transition-transform hover:-translate-y-0.5">
                    Re-order now <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recent orders */}
      <CombineUnpaid mode="account" />
      <section className="mt-10" data-testid="trade-orders">
        <h2 className="font-heading text-lg font-black tracking-tight">Your recent orders</h2>
        {orders.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="trade-orders-empty">
            No orders yet. When you place one, it'll show here with its tracking PIN.
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            {orders.map((o) => (
              <div key={o.id} data-testid={`trade-order-${o.order_number}`} className="flex flex-wrap items-center justify-between gap-3 border border-border bg-card p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="mono text-sm font-bold">{o.order_number}</span>
                    <span className="mono rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
                      {STATUS_LABEL[o.payment_status] || o.payment_status}
                    </span>
                  </div>
                  <div className="mono mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                    <span>{new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    <span className="inline-flex items-center gap-1"><Truck className="h-3 w-3" /> {FULFILMENT_LABEL[o.fulfillment_status] || o.fulfillment_status}</span>
                    {o.tracking_pin && <span>PIN <b className="text-foreground">{o.tracking_pin}</b></span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-heading text-lg font-black">{gbp(o.total)}</span>
                  <Link to={`/order/${o.id}?token=${o.access_token}`} data-testid={`trade-order-view-${o.order_number}`}
                    className="mono text-[11px] font-bold uppercase tracking-[0.15em] text-primary hover:underline">
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
